import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2
} from "@angular/core";
import { TransferState } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { CLIENT_IP, CLIENT_IP_KEY } from "@app/client-ip.injector";
import { SetBreadcrumb } from "@app/store/actions/breadcrumb.actions";
import { FetchMessagesUnreadCount } from "@app/store/actions/messages-unread-count.actions";
import { selectRequestCountry } from "@app/store/selectors/app/app.selectors";
import { MainState } from "@app/store/state";
import { JsonApiService } from "@core/services/api/classic/json/json-api.service";
import { AuthService } from "@core/services/auth.service";
import { CookieConsentService } from "@core/services/cookie-consent/cookie-consent.service";
import { DeviceService } from "@core/services/device.service";
import { FooterVisibilityService } from "@core/services/footer-visibility.service";
import { IdleService } from "@core/services/idle.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { ThemeService } from "@core/services/theme.service";
import { TitleService } from "@core/services/title/title.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { VersionCheckService } from "@core/services/version-check.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { CookieConsentEnum } from "@core/types/cookie-consent.enum";
import { NotificationsApiService } from "@features/notifications/services/notifications-api.service";
import { GetUnreadCount } from "@features/notifications/store/notifications.actions";
import { NgbModal, NgbOffcanvas, NgbPaginationConfig } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { TutorialTooltipComponent } from "@shared/components/misc/tutorial/tutorial-tooltip.component";
import { Constants } from "@shared/constants";
import { Observable, of, Subscription, timer } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from "rxjs/operators";

declare let dataLayer: any;
declare let gtag: any;

@Component({
  selector: "astrobin-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"]
})
export class AppComponent extends BaseComponentDirective implements OnInit, OnDestroy {
  showFooter = true;
  private readonly _isBrowser: boolean;
  private _serviceWorkerKillSwitchSubscription: Subscription;
  private _tutorialTooltipComponentRef: ComponentRef<TutorialTooltipComponent>;
  private _lastUnreadCountUpdate = 0;
  private _messagesUnreadCountPollingSubscription: Subscription;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly router: Router,
    public readonly paginationConfig: NgbPaginationConfig,
    public readonly themeService: ThemeService,
    public readonly windowRefService: WindowRefService,
    public readonly notificationApiService: NotificationsApiService,
    public readonly cookieConsentService: CookieConsentService,
    public readonly utilsService: UtilsService,
    @Inject(PLATFORM_ID) public readonly platformId: any,
    public readonly renderer: Renderer2,
    @Inject(DOCUMENT) public document: any,
    public readonly transferState: TransferState,
    @Inject(CLIENT_IP) public readonly clientIp: string,
    public readonly offcanvasService: NgbOffcanvas,
    public readonly loadingService: LoadingService,
    public readonly titleService: TitleService,
    public readonly versionCheckService: VersionCheckService,
    public readonly jsonApiService: JsonApiService,
    public readonly modalService: NgbModal,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly idleService: IdleService,
    public readonly footerVisibilityService: FooterVisibilityService,
    public readonly deviceService: DeviceService,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly applicationRef: ApplicationRef,
    private readonly authService: AuthService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super(store$);

    this._isBrowser = isPlatformBrowser(platformId);

    if (!this._isBrowser) {
      // On the server-side, add the IP to the TransferState
      this.transferState.set(CLIENT_IP_KEY, this.clientIp);
    }

    this.initRouterEvents();
    this.initPagination();
    this.markNotificationAsRead();
    this.themeService.setTheme();
  }

  ngOnInit(): void {
    this._handleServiceWorkerTransition();
    this._startPollingForServiceWorkerKillSwitch();
    this._suppressPwaInstallationPrompt();
    this._initGoogleAnalytics();
    this._initializeVersionChecking();
    this._checkPwaAuthentication();
    this._setPwaBodyClass();

    if (this._isBrowser && !this._isInIframe()) {
      this._createTutorialTooltip();
    }

    // Subscribe to footer visibility only in browser
    if (this._isBrowser) {
      this.footerVisibilityService.footerVisible$
        .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
        .subscribe((visible: boolean) => {
          this.showFooter = visible;
          this.changeDetectorRef.markForCheck();
        });

      // Start polling for messages unread count immediately and then every 30 seconds
      this._messagesUnreadCountPollingSubscription = timer(0, 30000)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(() => {
          this.store$.dispatch(new FetchMessagesUnreadCount());
        });
    }
  }

  ngOnDestroy(): void {
    if (this._serviceWorkerKillSwitchSubscription) {
      this._serviceWorkerKillSwitchSubscription.unsubscribe();
    }

    if (this._messagesUnreadCountPollingSubscription) {
      this._messagesUnreadCountPollingSubscription.unsubscribe();
    }

    this._destroyTutorialTooltip();
  }

  initPagination(): void {
    this.paginationConfig.pageSize = 50;
    this.paginationConfig.maxSize = 5;
    this.paginationConfig.rotate = true;
  }

  initRouterEvents(): void {
    let previousUrl: string | null = null;

    this.router.events?.subscribe(event => {
      if (event instanceof NavigationEnd) {
        // Get URLs without fragments
        const currentUrlWithoutFragment = this._getUrlWithoutFragment(event.urlAfterRedirects);
        const previousUrlWithoutFragment = previousUrl ? this._getUrlWithoutFragment(previousUrl) : null;

        // Only clear breadcrumb if base URL changed (ignoring fragment)
        if (currentUrlWithoutFragment !== previousUrlWithoutFragment) {
          this.store$.dispatch(new SetBreadcrumb({ breadcrumb: [] }));

          // Only show footer when navigating to a different route, not within the same route
          // This prevents overriding components that hide the footer (like image edit wizard)
          this.footerVisibilityService.show();
        }

        // Regular cleanup operations
        if (this.offcanvasService.hasOpenOffcanvas()) {
          this.offcanvasService.dismiss();
        }

        if (this.modalService.hasOpenModals()) {
          this.modalService.dismissAll();
        }

        this.popNotificationsService.clear();
        this.windowRefService.changeBodyOverflow("auto");
        this.tagGoogleAnalyticsPage(event.urlAfterRedirects);
        this.setCanonicalUrl(event.urlAfterRedirects);
        this.setMetaTags(event.urlAfterRedirects);

        if (this._isBrowser) {
          this.utilsService
            .delay(500)
            .pipe(
              switchMap(() => this.currentUser$),
              filter(currentUser => !!currentUser),
              take(1)
            )
            .subscribe(() => {
              // Throttle notification count updates to once per 30 seconds
              const now = Date.now();
              const thirtySecondsInMs = 30 * 1000;

              if (now - this._lastUnreadCountUpdate > thirtySecondsInMs) {
                this._lastUnreadCountUpdate = now;
                this.store$.dispatch(new GetUnreadCount());
              }
            });
        }

        // Update previous URL for next navigation
        previousUrl = event.urlAfterRedirects;
      }
    });
  }

  includeAnalytics$(): Observable<boolean> {
    return this.store$.select(selectRequestCountry).pipe(
      filter(requestCountry => !!requestCountry),
      take(1),
      map(requestCountry => {
        const isGDPRCountry = UtilsService.isGDPRCountry(requestCountry);
        const hasCookieConsent = this.cookieConsentService.cookieGroupAccepted(CookieConsentEnum.ANALYTICS);

        return !isGDPRCountry || hasCookieConsent;
      })
    );
  }

  tagGoogleAnalyticsPage(url: string): void {
    if (!this._isBrowser) {
      return;
    }

    if (typeof gtag !== "undefined") {
      this.includeAnalytics$().subscribe(includeAnalytics => {
        if (includeAnalytics) {
          gtag("config", Constants.GOOGLE_ANALYTICS_ID, {
            page_path: url
          });
        }
      });
    }
  }

  setCanonicalUrl(url: string): void {
    const canonicalUrl =
      this.windowRefService.nativeWindow.location.protocol +
      "//" +
      this.windowRefService.nativeWindow.location.host +
      url;
    const link: HTMLLinkElement = this.document.createElement("link");

    link.setAttribute("rel", "canonical");
    this.document.head.appendChild(link);
    link.setAttribute("href", canonicalUrl);
  }

  setMetaTags(url: string): void {
    this.titleService.addMetaTag({
      property: "og:type",
      content: "website"
    });
    this.titleService.addMetaTag({
      property: "og:url",
      content:
        this.windowRefService.nativeWindow.location.protocol +
        "//" +
        this.windowRefService.nativeWindow.location.host +
        url
    });
  }

  markNotificationAsRead() {
    if (!this._isBrowser) {
      return;
    }

    this.currentUser$
      .pipe(
        filter(currentUser => !!currentUser),
        take(1)
      )
      .subscribe(() => {
        const url = this.windowRefService.getCurrentUrl();

        if (!!url && url.searchParams.get("utm_medium") === "email") {
          const fromUserPk = url.searchParams.get("from_user");
          this.notificationApiService
            .markAsReadByPathAndUser(url.pathname, fromUserPk !== "None" ? +fromUserPk : null)
            .subscribe();
        }
      });
  }

  private _createTutorialTooltip(): void {
    if (!this._isBrowser) {
      return;
    }
    // Create component factory
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(TutorialTooltipComponent);

    // Create component using the factory
    this._tutorialTooltipComponentRef = componentFactory.create(this.injector);

    // Attach component to the appRef so that it's inside Angular's component tree
    this.applicationRef.attachView(this._tutorialTooltipComponentRef.hostView);

    // Get DOM element from component
    const domElem = (this._tutorialTooltipComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    // Append to the body (only in browser context)
    this.document.body.appendChild(domElem);
  }

  private _destroyTutorialTooltip(): void {
    if (!this._isBrowser || this._isInIframe()) {
      return;
    }

    if (this._tutorialTooltipComponentRef) {
      this.applicationRef.detachView(this._tutorialTooltipComponentRef.hostView);
      this._tutorialTooltipComponentRef.destroy();
      this._tutorialTooltipComponentRef = null;
    }
  }

  private _getUrlWithoutFragment(url: string): string {
    if (!this._isBrowser) {
      return;
    }

    const urlObject = new URL(url, this.windowRefService.nativeWindow.location.origin);
    urlObject.hash = "";
    return urlObject.pathname + urlObject.search;
  }

  private _startPollingForServiceWorkerKillSwitch(): void {
    if (!this._isBrowser) {
      return;
    }

    // Start immediately and poll every 60 seconds.
    this._serviceWorkerKillSwitchSubscription = timer(0, 60000)
      .pipe(
        // Only proceed if the user was active within the idle threshold.
        filter(() => !this.idleService.isUserIdle()),
        switchMap(() =>
          this.jsonApiService.serviceWorkerEnabled().pipe(
            catchError(err => {
              console.error("Kill switch API error:", err);
              return of(null); // Emit null so we can filter it later.
            })
          )
        ),
        filter((enabled): enabled is boolean => enabled !== null)
      )
      .subscribe((enabled: boolean) => {
        if (!enabled) {
          this._disableServiceWorker();
        } else {
          this._enableServiceWorker();
        }
      });
  }

  private _disableServiceWorker(): void {
    if (!this._isBrowser) {
      return;
    }

    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistration().then(registration => {
        if (registration) {
          void registration.unregister();
        }
      });
    }
  }

  private _enableServiceWorker(): void {
    if (!this._isBrowser) {
      return;
    }

    this.versionCheckService.checkForUpdates();
  }

  private _suppressPwaInstallationPrompt(): void {
    if (this._isBrowser) {
      this.windowRefService.nativeWindow.addEventListener("beforeinstallprompt", event => {
        event.preventDefault();
      });
    }
  }

  private _initializeVersionChecking(): void {
    if (!this._isBrowser) {
      return;
    }

    // Check if service worker is already ready
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      this.versionCheckService.checkForUpdates();
    } else if ("serviceWorker" in navigator) {
      // Wait for service worker to be ready
      navigator.serviceWorker.ready
        .then(() => {
          this.versionCheckService.checkForUpdates();
        })
        .catch(err => {
          console.error("[AppComponent] Error waiting for service worker:", err);
        });
    }
  }

  private _initGoogleAnalytics(): void {
    if (this._isBrowser && Object.keys(this.windowRefService.nativeWindow).indexOf("Cypress") === -1) {
      this.includeAnalytics$().subscribe(includeAnalytics => {
        if (includeAnalytics) {
          this.utilsService.insertScript(
            `https://www.googletagmanager.com/gtag/js?id=${Constants.GOOGLE_ANALYTICS_ID}`,
            this.renderer
          );
          this.utilsService.insertScript(
            `https://www.googletagmanager.com/gtm.js?id=${Constants.GOOGLE_TAG_MANAGER_ID}`,
            this.renderer,
            () => {
              this._initGtag();
            }
          );
        }
      });
    }
  }

  private _initGtag(): void {
    if (!this._isBrowser) {
      return;
    }

    try {
      dataLayer = dataLayer || [];
    } catch (e) {
      return;
    }

    // @ts-ignore
    gtag("js", new Date());

    // @ts-ignore
    gtag("config", Constants.GOOGLE_ANALYTICS_ID, {
      linker: {
        domains: [
          "www.astrobin.com",
          "app.astrobin.com",
          "welcome.astrobin.com",
          "de.welcome.astrobin.com",
          "es.welcome.astrobin.com",
          "fr.welcome.astrobin.com",
          "it.welcome.astrobin.com",
          "pt.welcome.astrobin.com"
        ],
        accept_incoming: true
      }
    });

    // @ts-ignore
    gtag("config", "AW-1062298010");
  }

  /**
   * Detects if the application is running inside an iframe
   * @returns true if running in an iframe, false otherwise
   */
  private _isInIframe(): boolean {
    if (!this._isBrowser) {
      return false; // On server-side, assume not in iframe
    }

    try {
      return this.windowRefService.nativeWindow.self !== this.windowRefService.nativeWindow.top;
    } catch (e) {
      // Cross-origin iframe throws an error when accessing window.top
      return true;
    }
  }

  private _handleServiceWorkerTransition(): void {
    if (!this._isBrowser || !("serviceWorker" in navigator)) {
      return;
    }

    // Check for old custom service worker and migrate to standard one
    void navigator.serviceWorker.getRegistrations().then(registrations => {
      registrations.forEach(registration => {
        const sw = registration.active || registration.installing || registration.waiting;
        if (sw && sw.scriptURL.includes("sw-custom.js")) {
          // Old custom service worker found, unregister it
          void registration.unregister().then(() => {
            // Clear all caches to ensure clean state
            if ("caches" in window) {
              void caches.keys().then(names => {
                names.forEach(name => void caches.delete(name));
              });
            }
            // Force reload to register new service worker
            window.location.reload();
          });
        }
      });
    });
  }

  private _checkPwaAuthentication(): void {
    if (!this._isBrowser) {
      return;
    }

    if (this.deviceService.isPwa()) {
      this.authService
        .isAuthenticated$()
        .pipe(take(1))
        .subscribe(isAuthenticated => {
          if (!isAuthenticated) {
            // Use window.location.pathname instead of router.url since router hasn't navigated yet
            const currentUrl = this.windowRefService.nativeWindow.location.pathname;
            const authRoutes = [
              "/account/login",
              "/account/logout",
              "/account/register",
              "/account/activate",
              "/account/verify-2fa",
              "/account/logged-in",
              "/account/logged-out",
              "/account/logging-in",
              "/account/logging-out",
              "/account/password-reset"
            ];

            // Check if current route is an auth route
            const isAuthRoute = authRoutes.some(route => currentUrl.startsWith(route));

            if (!isAuthRoute) {
              void this.router.navigate(["/account/login"], {
                queryParams: currentUrl && currentUrl !== "/" ? { redirectUrl: currentUrl } : {}
              });
            }
          }
        });
    }
  }

  private _setPwaBodyClass(): void {
    if (!this._isBrowser) {
      return;
    }

    if (this.deviceService.isPwa()) {
      this.renderer.addClass(this.document.body, "pwa-mode");
      this.renderer.addClass(this.document.documentElement, "pwa-mode");

      // Disable zoom for PWA mode using TitleService
      this.titleService.disablePwaZoom();
    }
  }
}
